import { FetchArgs } from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { captureException, configureScope, Severity } from '@sentry/react';
import { BASE_API_URL } from 'config.js';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { RootState } from 'store';
import { addProductMeta } from 'store/useProductMetaSlice';
import { downloadBlob } from 'utils/helpers';
import type {
  CampaignRevenueAttributionData,
  CustomersList,
  CustomerStatistics,
  CustomerStatisticsKeys,
  DashboardLifecycle,
  DashboardStoreAgg,
  EmailCampaign,
  EmailTimeSeries,
  Filters,
  GoogleCustomerIdResponses,
  JourneyOrdersPath,
  JourneyProductList,
  JourneySummary,
  KlaviyoListIdResponses,
  newCohort,
  OnboardingProgressStatus,
  ProductCrossSell,
  ProductImage,
  ProductReplenishment,
  PurchaseTiming,
  Referrals,
  RelationsProductList,
  RelationsSummary,
  SegmentInfo,
  SegmentsAndCategories,
  StoreInfo,
  SubscriptionPlan,
  UserInfo,
} from './types';

const headersWithToken = (
  headers: Headers,
  api: { getState: () => unknown }
) => {
  const token = (api.getState() as RootState).userDetails.token;

  if (token) {
    headers.set('authorization', `Bearer ${token}`);
  }

  return headers;
};

const notRetryCodes = [400, 401, 404, 405, 500, 'PARSING_ERROR', 'FETCH_ERROR'];

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const sleeps = [5000, 7000, 15000, 20000, 25000];

async function defaultBackoff(attempt: number = 0, maxRetries: number = 5) {
  const ms = sleeps[attempt] + (Math.random() + 0.8);
  await sleep(ms);
}

const baseQueryWithRetryAndBailOut = retry(
  async (args: string | FetchArgs, api, extraOptions) => {
    const result = await fetchBaseQuery({
      baseUrl: String(BASE_API_URL),
      prepareHeaders: headersWithToken,
    })(args, api, extraOptions);
    if (result.error?.status && notRetryCodes.includes(result.error.status)) {
      retry.fail(result.error);
    }
    return result;
  },
  {
    maxRetries: 3,
    backoff: defaultBackoff,
  }
);

export const segmentsApi = createApi({
  reducerPath: 'segmentsApi',
  tagTypes: [
    'ProductsCohort',
    'UserInfo',
    'StoreInfo',
    'Referrals',
    'StoresList',
    'CustomerCohorts',
    'CustomerPurchaseTiming',
    'DashboardStoreAgg',
    'DashboardLifecycle',
    'DashboardEmailCampaign',
    'EmailTimeSeries',
    'CustomerSegments',
    'FilterTags',
    'ProductImage',
  ],
  baseQuery: baseQueryWithRetryAndBailOut,
  keepUnusedDataFor: 600,
  endpoints: (builder) => ({
    // * /account endpoints start
    // GET /account/user
    getUserInfo: builder.query<UserInfo, void>({
      query: () => '/account/user',
      transformResponse: (response: { data: UserInfo }) => response.data,
      providesTags: ['UserInfo'],
    }),
    // GET /account/stores
    getStores: builder.query<StoreInfo[], void>({
      query: () => `/account/stores`,
      transformResponse: (response: { stores: StoreInfo[] }) => response.stores,
      providesTags: ['StoresList'],
    }),
    // GET /account/store
    getStoreInfo: builder.query<StoreInfo, string>({
      query: (serializedStoreName) => `/account/store?${serializedStoreName}`,
      transformResponse: (response: { store: StoreInfo }) => response.store,
      providesTags: (result, error, serializedStoreName) => [
        { type: 'StoreInfo', id: serializedStoreName },
      ],
    }),
    // GET /account/referrals
    getReferrals: builder.query<Referrals, void>({
      query: () => `/account/referrals`,
      providesTags: ['Referrals'],
    }),
    // GET /filters
    getFilters: builder.query<Filters[], { serializedParams: string }>({
      query: ({ serializedParams }) => `/v2/filters/?${serializedParams}`,
      transformResponse: (response: { data: Filters[] }) => response.data,
      providesTags: ['FilterTags'],
    }),
    // PUT /account/change-name
    updateUserName: builder.mutation<void, { new_name: string }>({
      query: (body) => ({
        url: '/account/change-name',
        method: 'PUT',
        body,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['UserInfo'],
      onQueryStarted({ new_name }, { queryFulfilled, dispatch }) {
        const patchResult = dispatch(
          segmentsApi.util.updateQueryData(
            'getUserInfo',
            undefined,
            (draft) => {
              draft['name'] = new_name;
            }
          )
        );
        queryFulfilled
          .then(() => toast.success('Name updated successfully'))
          .catch(patchResult.undo);
      },
    }),
    // POST /account/change-email
    updateUserEmail: builder.mutation<void, { new_email: string }>({
      query: (body) => ({
        url: '/account/change-email',
        method: 'POST',
        body,
      }),
      extraOptions: { maxRetries: 0 },
      onQueryStarted(body, { queryFulfilled, dispatch }) {
        queryFulfilled.then(() =>
          toast.success('Verification link sent to the new email')
        );
      },
    }),
    // POST account/confirm
    sendAccountVerificationLink: builder.mutation<void, void>({
      query: () => ({
        url: '/account/confirm',
        method: 'POST',
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['UserInfo'],
      onQueryStarted(args, { queryFulfilled, dispatch }) {
        queryFulfilled.then(() =>
          toast.success('Account verification link sent to the email')
        );
      },
    }),
    // PUT account/change-email/{token}
    confirmEmailChange: builder.mutation<void, { token: string }>({
      query: ({ token }) => ({
        url: `/account/change-email/${token}`,
        method: 'PUT',
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['UserInfo'],
      onQueryStarted(args, { queryFulfilled, dispatch }) {
        queryFulfilled.then(() =>
          toast.success('Email confirmed successfully')
        );
      },
    }),
    // PUT account/confirm/{token}
    confirmAccount: builder.mutation<void, { token: string }>({
      query: ({ token }) => ({
        url: `/account/confirm/${token}`,
        method: 'PUT',
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['UserInfo'],
      onQueryStarted(args, { queryFulfilled, dispatch }) {
        queryFulfilled.then(() =>
          toast.success('Account verified successfully')
        );
      },
    }),
    // POST account/stores/add-member
    addStoreMember: builder.mutation<
      void,
      {
        payload: { email: string; store_name: string };
        serializedStoreName: string;
      }
    >({
      query: ({ payload }) => ({
        url: '/account/stores/add-member',
        method: 'POST',
        body: payload,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (result, error, { serializedStoreName }) => [
        { type: 'StoreInfo', id: serializedStoreName },
      ],
      onQueryStarted(
        { payload, serializedStoreName },
        { queryFulfilled, dispatch }
      ) {
        const patchResult = dispatch(
          segmentsApi.util.updateQueryData(
            'getStoreInfo',
            serializedStoreName,
            (draft) => {
              draft.users.unshift({
                id: payload.email,
                email: payload.email,
                name: '',
                role_name: '',
              });
            }
          )
        );
        queryFulfilled
          .then(() => toast.success(`User added/invited to the store`))
          .catch(patchResult.undo);
      },
    }),
    // DELETE /account/stores/remove-member/{store_id}/{user_id}
    removeStoreMember: builder.mutation<
      void,
      { serializedStoreName: string; storeId: number; memberId: number }
    >({
      query: ({ storeId, memberId }) => ({
        url: `/account/stores/remove-member/${storeId}/${memberId}`,
        method: 'DELETE',
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (result, error, { serializedStoreName }) => [
        { type: 'StoreInfo', id: serializedStoreName },
      ],
      onQueryStarted(args, { queryFulfilled }) {
        queryFulfilled.then(() => toast.success(`User removed successfully.`));
      },
    }),
    // PUT account/change-password
    updatePassword: builder.mutation<
      void,
      { old_password: string; new_password: string }
    >({
      query: (body) => ({
        url: `/account/change-password`,
        method: 'PUT',
        body,
      }),
      extraOptions: { maxRetries: 0 },
      onQueryStarted(args, { queryFulfilled }) {
        queryFulfilled
          .then(() => toast.success('Password updated successfully'))
          .catch((error) =>
            toast.error(
              error.data?.error_reason ||
                error.data?.message ||
                'Failed to perform the action'
            )
          );
      },
    }),
    // POST account/reset-password
    sendResetPasswordLink: builder.mutation<void, { email: string }>({
      query: (body) => ({
        url: '/account/reset-password',
        method: 'POST',
        body,
      }),
      extraOptions: { maxRetries: 0 },
      onQueryStarted(args, { queryFulfilled }) {
        queryFulfilled.then(() =>
          toast.success('Reset password link sent to the submitted email.', {
            autoClose: 10000,
          })
        );
      },
    }),
    // PUT account/reset-password/{token}
    setNewPassword: builder.mutation<
      void,
      {
        token: string;
        body: { email: string; new_password: string };
      }
    >({
      query: ({ body, token }) => ({
        url: `/account/reset-password/${token}`,
        method: 'PUT',
        body,
      }),
      extraOptions: { maxRetries: 0 },
      onQueryStarted(args, { queryFulfilled }) {
        queryFulfilled.then(() => toast.success('Password set successfully.'));
      },
    }),
    // PUT /account/invitation/
    confirmInvitation: builder.mutation<
      void,
      {
        body: {
          name: string;
          password: string;
        };
        token: string;
        userId: number;
        storeName: string;
      }
    >({
      query: ({ body, token, userId, storeName }) => ({
        url: `/account/invitation/${userId}/${token}`,
        method: 'PUT',
        body,
      }),
      extraOptions: { maxRetries: 0 },
      onQueryStarted({ body, storeName }, { queryFulfilled }) {
        queryFulfilled.then(() =>
          toast.success(
            `User ${body.name} added to store ${storeName}. You will be redirected to Log in.`
          )
        );
      },
    }),
    // * /account endpoints ends
    // * /customers endpoints start
    // POST /v2/customers/cohort
    getCustomerCohorts: builder.query<
      newCohort,
      { serializedParams: string; body: Record<string, any> }
    >({
      query: ({ serializedParams, body }) => ({
        url: `/v2/customers/cohort?${serializedParams}`,
        method: 'POST',
        body,
      }),
      providesTags: ['CustomerCohorts'],
    }),
    getProductsCohorts: builder.query<
      newCohort,
      { serializedParams: string; body: Record<string, any> }
    >({
      query: ({ serializedParams, body }) => ({
        url: `/v2/products/cohort?${serializedParams}`,
        method: 'POST',
        body,
      }),
      providesTags: ['ProductsCohort'],
    }),
    // GET /v2/customers/purchase_timing
    getCustomerPurchaseTiming: builder.query<
      PurchaseTiming,
      { serializedParams: string; body: Record<string, any> }
    >({
      query: ({ serializedParams, body }) => ({
        url: `/v2/customers/purchase_timing?${serializedParams}`,
        method: 'POST',
        body,
      }),
      providesTags: ['CustomerPurchaseTiming'],
    }),
    // * /customers endpoints end
    // * /Dashboard - Dashboard endpoints start
    // GET /dashboard/storeagg
    getDashboardStoreAgg: builder.query<
      DashboardStoreAgg,
      { serializedStoreName: string }
    >({
      query: ({ serializedStoreName }) =>
        `/dashboard/storeagg?${serializedStoreName}`,
      transformResponse: (response: { store_agg: DashboardStoreAgg }) =>
        response.store_agg,
      providesTags: ['DashboardStoreAgg'],
    }),
    // GET /dashboard/lifecycle
    getDashboardLifecycle: builder.query<
      { segments: SegmentsAndCategories; lifecycle: DashboardLifecycle },
      { serializedStoreName: string }
    >({
      query: ({ serializedStoreName }) =>
        `/dashboard/lifecycle?${serializedStoreName}`,
      transformResponse: (response: { data: DashboardLifecycle }) => {
        const segments = {
          at_risk_new_signups: response?.data?.risk_segments[0],
          at_risk_one_timers: response?.data?.risk_segments[1],
          at_risk_repeats: response?.data?.risk_segments[2],
          at_risk_loyals: response?.data?.risk_segments[3],
          active_new_signups: response?.data?.active_segments[0],
          active_one_timers: response?.data?.active_segments[1],
          active_repeats: response?.data?.active_segments[2],
          active_loyals: response?.data?.active_segments[3],
          churned_signups: response?.data?.churn_segments[0],
          churned_one_timers: response?.data?.churn_segments[1],
          churned_repeats: response?.data?.churn_segments[2],
          churned_loyals: response?.data?.churn_segments[3],
        };
        return {
          segments,
          lifecycle: response.data,
        };
      },

      providesTags: ['DashboardLifecycle'],
    }),
    // GET /dashboard/campaign
    getDashboardEmailCampaign: builder.query<
      EmailCampaign,
      { serializedStoreName: string }
    >({
      query: ({ serializedStoreName }) =>
        `/dashboard/campaign?${serializedStoreName}`,
      transformResponse: (response: { campaign: EmailCampaign }) =>
        response.campaign,
      providesTags: ['DashboardEmailCampaign'],
    }),
    // GET /email/timeseries
    getEmailTimeSeries: builder.query<
      EmailTimeSeries[],
      { serializedParams: string }
    >({
      query: ({ serializedParams }) => `/emails/timeseries?${serializedParams}`,
      transformResponse: (response: { data: EmailTimeSeries[] }) =>
        response.data,
      providesTags: ['EmailTimeSeries'],
    }),

    getEmailCampaignRevenueAttribution: builder.query<
      CampaignRevenueAttributionData,
      { serializedStoreName: string }
    >({
      query: ({ serializedStoreName }) =>
        `/emails/revenue?${serializedStoreName}`,
      transformResponse: (response: { data: CampaignRevenueAttributionData }) =>
        response.data,
    }),
    // * /Customers - Customers endpoints start
    // GET /customers/segments
    getCustomerSegments: builder.query<
      SegmentInfo[],
      { serializedStoreName: string }
    >({
      query: ({ serializedStoreName }) =>
        `/customers/segments?${serializedStoreName}`,
      transformResponse: (response: { person_segments: SegmentInfo[] }) =>
        response.person_segments,
      providesTags: (result, error, params) =>
        result
          ? result.map(({ id }) => ({
              type: 'CustomerSegments',
              id: id,
            }))
          : ['CustomerSegments'],
    }),
    // PUT /customers/segments/{segmentId}?{StoreName}
    // Used to update favorite and auto-sync at the moment
    updateSegment: builder.mutation<
      void,
      {
        segmentId: number;
        serializedStoreName: string;
        body: Record<string, any>;
      }
    >({
      query: ({ segmentId, body, serializedStoreName }) => ({
        url: `/customers/segments/${segmentId}?${serializedStoreName}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { segmentId }) => [
        { type: 'CustomerSegments', id: segmentId },
      ],
      extraOptions: { maxRetries: 0 },
      onQueryStarted(
        { segmentId, body, serializedStoreName },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          segmentsApi.util.updateQueryData(
            'getCustomerSegments',
            { serializedStoreName },
            (draft) => {
              const segmentIndex = draft.findIndex(
                (item) => item.id === segmentId
              );

              const integrationFields = ['auto_synced'];
              for (let key in body) {
                if (integrationFields.includes(key)) {
                  const current = draft[segmentIndex].integrations.find(
                    (item) => item.id === body.integration_id
                  );
                  // @ts-ignore
                  current[key] = !!!current[key];

                  return;
                }
                // @ts-ignore
                draft[segmentIndex][key] = !!!draft[segmentIndex][key];
              }
            }
          )
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),

    // POST /segment/{EspProvier}/sync?{StoreName}{SegmentId}
    syncOnce: builder.mutation<
      void,
      {
        provider: string;
        rawParams: {
          store_name: string;
          segment_id: string;
          integration_id: string;
        };
      }
    >({
      query: ({ provider, rawParams }) => ({
        url: `/segment/${provider}/sync?${new URLSearchParams(rawParams)}`,
        method: 'POST',
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (result, error, { rawParams }) => [
        { type: 'CustomerSegments', id: Number(rawParams.segment_id) },
      ],
      onQueryStarted({ rawParams }, { queryFulfilled }) {
        queryFulfilled.then(() => toast.success('Segment synced successfully'));
      },
    }),
    // POST customers/segments
    saveSegment: builder.mutation<
      void,
      { serializedParams: string; body: Pick<any, string | number | symbol> }
    >({
      query: ({ serializedParams, body }) => ({
        url: `/v2/customers/segments?${serializedParams}`,
        method: 'POST',
        body,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['CustomerSegments'],
      onQueryStarted(args, { queryFulfilled }) {
        queryFulfilled.then(() => toast.success('Segment saved successfully.'));
      },
    }),

    // DELETE customers/segments
    deleteSegment: builder.mutation<
      void,
      { segmentId: number; serializedStoreName: string }
    >({
      query: ({ serializedStoreName, segmentId }) => ({
        url: `/customers/segments/${segmentId}?${serializedStoreName}`,
        method: 'DELETE',
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['CustomerSegments'],
      onQueryStarted(args, { queryFulfilled }) {
        queryFulfilled.then(() =>
          toast.success('Segment deleted successfully.')
        );
      },
    }),
    // GET /onboarding/onboarding_progress_bar
    getOnboardingProgressStatus: builder.query<
      OnboardingProgressStatus,
      { serializedStoreName: string }
    >({
      query: ({ serializedStoreName }) =>
        `/onboarding/onboarding_progress_bar?${serializedStoreName}`,
    }),

    // POST /onboarding/onboarding_settings
    updateOnboardingSettings: builder.mutation<
      OnboardingProgressStatus,
      {
        serializedStoreName: string;
        body: {
          objectives?: number[];
          segment_names?: string[];
          integrations?: string[];
          job_title?: number | null;
          staff_size?: number | null;
          channel_via?: number[];
        };
      }
    >({
      query: ({ serializedStoreName, body }) => ({
        url: `/onboarding/onboarding_settings?${serializedStoreName}`,
        method: 'POST',
        body,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['UserInfo', 'StoreInfo', 'CustomerSegments'],
    }),
    // POST /v2/customers/statistics
    getCustomerStatistics: builder.query<
      Partial<CustomerStatistics>,
      {
        body?: Record<string, any>;
        params: {
          metric_list: CustomerStatisticsKeys;
          /** eg.: "aeto labs"*/
          store_name: string;
          /** string yyyy-MM-dd */
          start_date?: string;
          /** string yyyy-MM-dd */
          end_date?: string;
          ticking?: 'week' | 'day' | 'month';
        };
      }
    >({
      query: ({ body = {}, params }) => ({
        url: `/v2/customers/statistics?${new URLSearchParams({
          ...params,
          store_name: params.store_name,
          metric_list: params.metric_list.toString(),
        }).toString()}`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: Partial<CustomerStatistics> }) =>
        response.data,
    }),

    // POST /v2/customers/
    getCustomersList: builder.query<
      CustomersList,
      { serializedParams: string; body: Record<string, any> }
    >({
      query: ({ serializedParams, body }) => ({
        url: `/v2/customers?${serializedParams}`,
        method: 'POST',
        body,
      }),
    }),
    // GET /customers/ - Export CSV / FACEBOOK / GOOGLE
    exportCustomersList: builder.mutation<
      any,
      {
        serializedParams: string;
        body: any;
        provider?: string;
        segmentName?: string;
      }
    >({
      async queryFn(
        { serializedParams, body, provider, segmentName },
        { getState }
      ) {
        const token = (getState() as RootState).userDetails.token;

        const args = {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(body),
        };

        const response = await fetch(
          BASE_API_URL + `/v2/customers?${serializedParams}`,
          args
        );

        if (!!!response.ok) {
          throw new Error('Failed to perform the task');
        }

        let filename;
        if (provider && segmentName) {
          filename = `${provider.toLowerCase()}-${segmentName
            .replace(/[^a-z\d]/gi, '_')
            .toLowerCase()}-${format(Date.now(), 'yyyy-MM-dd')}`;
        } else {
          filename = response.headers
            .get('content-disposition')
            ?.split(';')[1]
            .replace(' filename=', '');
        }

        const data = await response.blob();

        if (!!!data) {
          throw new Error('Failed to perform the task');
        }
        downloadBlob(data, filename);
        return { data: 'File downloaded succesfully' };
      },
      extraOptions: { maxRetries: 0 },
      onQueryStarted(args, { queryFulfilled }) {
        queryFulfilled
          .then(() => toast.success('File downloaded succesfully'))
          .catch(() =>
            toast.error(
              'Failed to export, please contact the team for assistance',
              { autoClose: 10000 }
            )
          );
      },
    }),
    // * /Products - Product journey start
    // POST /v2/products/journey/product-list
    getJourneyProductList: builder.query<
      JourneyProductList[],
      { serializedParams: string; body: Record<string, any> }
    >({
      query: ({ serializedParams, body }) => ({
        url: `/v2/products/journey/first-product-list?${serializedParams}`,
        method: 'POST',
        body,
      }),
    }),
    // POST /v2/products/journey/
    getProductJourney: builder.query<
      JourneyOrdersPath,
      { serializedParams: string; body: Record<string, any> }
    >({
      query: ({ serializedParams, body }) => ({
        url: `/v2/products/journey?${serializedParams}`,
        method: 'POST',
        body,
      }),
    }),
    getProductJourneySummary: builder.query<
      JourneySummary,
      { serializedParams: string; body: Record<string, any> }
    >({
      query: ({ serializedParams, body }) => ({
        url: `/v2/products/journey/summary?${serializedParams}`,
        method: 'POST',
        body,
      }),
    }),
    // * /Products - Product relations start
    // GET /products/journey/product-list
    getRelationsProductList: builder.query<
      RelationsProductList[],
      { serializedParams: string; body: Record<string, any> }
    >({
      query: ({ serializedParams, body }) => ({
        url: `/v2/products/product-relation-list?${serializedParams}`,
        method: 'POST',
        body,
      }),
    }),
    getRelationsSummary: builder.query<
      RelationsSummary,
      { serializedParams: string; body: Record<string, any> }
    >({
      query: ({ serializedParams, body }) => ({
        url: `/v2/products/product-relation-summary?${serializedParams}`,
        method: 'POST',
        body,
      }),
    }),
    // Product Relations - Cross Sell
    getProductCrossSell: builder.query<
      ProductCrossSell[],
      { serializedParams: string; productId: number; body: Record<string, any> }
    >({
      query: ({ serializedParams, productId, body }) => ({
        url: `/v2/products/cross-sell/${productId}?${serializedParams}`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { cross_sell: ProductCrossSell[] }) =>
        response.cross_sell,
    }),
    getProductReplenishment: builder.query<
      ProductReplenishment,
      { serializedParams: string; productId: number; body: Record<string, any> }
    >({
      query: ({ serializedParams, productId, body }) => ({
        url: `/v2/products/replenishment/${productId}?${serializedParams}`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { replenishment: ProductReplenishment }) =>
        response.replenishment,
    }),
    // /customers/segments - Export My segments list
    exportMySegmentsList: builder.mutation<any, { serializedParams: string }>({
      async queryFn({ serializedParams }, { getState }) {
        const token = (getState() as RootState).userDetails.token;

        const args = {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        };
        const response = await fetch(
          BASE_API_URL + `/v2/customers/segments?${serializedParams}`,
          args
        );

        if (!!!response.ok) {
          throw new Error('Failed to perform the task');
        }

        const filename = response.headers
          .get('content-disposition')
          ?.split(';')[1]
          .replace(' filename=', '');

        const data = await response.blob();

        if (!!!data) {
          throw new Error('Failed to perform the task');
        }
        downloadBlob(data, filename);
        return { data: 'File downloaded succesfully' };
      },
      extraOptions: { maxRetries: 0 },
      onQueryStarted(args, { queryFulfilled }) {
        queryFulfilled
          .then(() => toast.success('File downloaded succesfully'))
          .catch(() =>
            toast.error(
              'Failed to export, please contact the team for assistance',
              { autoClose: 10000 }
            )
          );
      },
    }),
    // POST integrations/{provider}/{storeId}
    addIntegration: builder.mutation<
      void,
      { storeId: number; provider: string; body: { [x: string]: string } }
    >({
      query: ({ storeId, provider, body }) => ({
        url: `/integrations/${provider}/${storeId}`,
        method: 'POST',
        body,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['StoreInfo', 'CustomerSegments', 'StoresList'],
      onQueryStarted(
        { body, provider, storeId },
        { queryFulfilled, getState }
      ) {
        queryFulfilled
          .then(() => toast.success('Provider connected successfully.'))
          .catch((error) => {
            configureScope((scope) => {
              scope.setExtra('error', error?.error);
              scope.setExtra('provider', provider);
              scope.setExtra('storeId', storeId);
              scope.setExtra('body', body);
              scope.setLevel(Severity.Error);
              scope.setTransactionName(`addIntegration - ${provider}`);
              scope.setUser({
                username:
                  (getState() as RootState).userDetails.loggedInUser ||
                  String(storeId),
              });
            });
            const errorMessage = new Error(
              `${provider} - ${
                error?.error?.data?.errors || 'Failed to connect'
              }`
            );
            errorMessage.name = 'Integration Error';
            captureException(errorMessage);
          });
      },
    }),
    // PUT inregrations/{provider}/{storeId} - update the customer list
    updateIntegration: builder.mutation<
      void,
      {
        storeId: number;
        provider: string;
        body: { [x: string]: string | string[] };
      }
    >({
      query: ({ storeId, provider, body }) => ({
        url: `/integrations/${provider}/${storeId}`,
        method: 'PUT',
        body,
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['StoreInfo', 'CustomerSegments', 'StoresList'],
      onQueryStarted(
        { body, provider, storeId },
        { queryFulfilled, getState }
      ) {
        queryFulfilled
          .then(() => toast.success('Provider connected successfully.'))
          .catch((error) => {
            configureScope((scope) => {
              scope.setExtra('error', error?.error || error);
              scope.setExtra('provider', provider);
              scope.setExtra('storeId', storeId);
              scope.setExtra('body', body);
              scope.setLevel(Severity.Error);
              scope.setTransactionName(`Update Integration - ${provider}`);
              scope.setUser({
                username:
                  (getState() as RootState).userDetails.loggedInUser ||
                  String(storeId),
              });
            });
            const errorMessage = new Error(
              `${provider} - ${
                error?.error?.data?.errors || 'Failed to Update integration'
              }`
            );
            errorMessage.name = 'Integration Error';
            captureException(errorMessage);
          });
      },
    }),
    // GET /integrations/{provider} - OAuth providers use GET API to integrate to our Backend
    addOAuthIntegration: builder.mutation<
      void,
      { provider: string; rawParams: { store_id: string; code: string } }
    >({
      query: ({ provider, rawParams }) => ({
        url: `/integrations/${provider}?${new URLSearchParams(rawParams)}`,
        method: 'GET',
      }),
      invalidatesTags: ['StoreInfo', 'CustomerSegments', 'StoresList'],
      extraOptions: { maxRetries: 0 },
      onQueryStarted({ provider, rawParams }, { queryFulfilled, getState }) {
        queryFulfilled.catch((error) => {
          configureScope((scope) => {
            scope.setExtra('error', error?.error || error);
            scope.setExtra('provider', provider);
            scope.setExtra('storeId', rawParams.store_id);
            scope.setLevel(Severity.Error);
            scope.setTransactionName(`Add Oauth Integration - ${provider}`);
            scope.setUser({
              username:
                (getState() as RootState).userDetails.loggedInUser ||
                String(rawParams.store_id),
            });
          });
          const errorMessage = new Error(
            `${provider} - ${
              (error as any)?.error?.data?.errors ||
              'Failed to Add Oauth integration'
            }`
          );
          errorMessage.name = 'Integration Error';
          captureException(errorMessage);
        });
      },
    }),
    // POST /integrations/{provider} - Post Google OAuth code to Backend and get the customer ids
    addGoogleOAuthIntegration: builder.mutation<
      GoogleCustomerIdResponses,
      { provider: string; rawParams: { store_id: string; code: string } }
    >({
      query: ({ provider, rawParams }) => ({
        url: `/integrations/google/${rawParams.store_id}`,
        method: 'POST',
        body: { code: rawParams.code },
      }),
      invalidatesTags: ['StoreInfo', 'CustomerSegments', 'StoresList'],
      extraOptions: { maxRetries: 0 },
      onQueryStarted({ provider, rawParams }, { queryFulfilled, getState }) {
        queryFulfilled.catch((error) => {
          configureScope((scope) => {
            scope.setExtra('error', error?.error || error);
            scope.setExtra('provider', provider);
            scope.setExtra('storeId', rawParams.store_id);
            scope.setLevel(Severity.Error);
            scope.setTransactionName(
              `Add Google Oauth Integration - ${provider}`
            );
            scope.setUser({
              username:
                (getState() as RootState).userDetails.loggedInUser ||
                String(rawParams.store_id),
            });
          });
          const errorMessage = new Error(
            `${provider} - ${
              (error as any)?.error?.data?.errors ||
              'Failed to Add Google Oauth integration'
            }`
          );
          errorMessage.name = 'Integration Error';
          captureException(errorMessage);
        });
      },
    }),
    // GET /klaviyo_lists/{store_id} - Get the data of the Klaviyo lists
    getKlaviyoList: builder.query<
      KlaviyoListIdResponses,
      { provider: string; storeId: string }
    >({
      query: ({ provider, storeId }) => ({
        url: `/integrations/klaviyo_lists/${storeId}`,
        method: 'GET',
      }),
    }),
    removeIntegration: builder.mutation<
      void,
      { storeId: number; provider: string }
    >({
      query: ({ storeId, provider }) => ({
        url: `/integrations/${provider}/${storeId}/remove`,
        method: 'DELETE',
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['StoreInfo', 'CustomerSegments'],
      onQueryStarted(args, { queryFulfilled }) {
        queryFulfilled.then(() =>
          toast.success('Provider integrations removed successfully.')
        );
      },
    }),
    getProductImage: builder.query<
      ProductImage[],
      { serializedStoreName: string; product_id: string }
    >({
      query: ({ product_id, serializedStoreName }) => ({
        url: `/v2/products/images?${serializedStoreName}&product_id=${product_id}`,
      }),
      providesTags: (result, error, params) => [
        { type: 'ProductImage', id: params.product_id },
      ],
      onQueryStarted(args, { queryFulfilled, dispatch }) {
        queryFulfilled.then((response) => {
          const productMeta =
            response.data.length > 0 ? response.data[0] : null;

          if (productMeta) {
            dispatch(
              addProductMeta({
                id: productMeta.product_id,
                image_url: productMeta.image_url ?? '',
                title: productMeta.title,
              })
            );
          }
        });
      },
    }),

    selectSubscriptionPlan: builder.mutation<
      { status: boolean; message: string; confirmation_url: string },
      { serializedStoreName: string; plan: SubscriptionPlan }
    >({
      query: ({ serializedStoreName, plan }) => ({
        url: `/integrations/shopify/${plan}?${serializedStoreName}`,
        method: 'PUT',
      }),
      extraOptions: { maxRetries: 0 },
    }),
    setStoreSubscriptionCharges: builder.mutation<
      void,
      { storeName: string; chargeId: string }
    >({
      query: ({ storeName, chargeId }) => ({
        url: `/integrations/charge?store_name=${storeName}&charge_id=${chargeId}`,
        method: 'POST',
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['UserInfo', 'StoreInfo', 'StoresList'],
    }),
    // POST /integrations/shopify - Add new Shopify store / Updates existing Store token
    addShopifyIntegration: builder.mutation<
      void,
      {
        shop: string;
        hmac: string;
        code: string;
        state: string;
        timestamp: string;
        host: string;
      }
    >({
      query: ({ code, hmac, host, shop, timestamp, state }) => ({
        url: `/integrations/shopify?shop=${shop}&hmac=${hmac}&code=${code}&state=${state}&timestamp=${timestamp}&host=${host}`,
        method: 'POST',
      }),
      extraOptions: { maxRetries: 0 },
      onQueryStarted(params, { queryFulfilled, getState }) {
        queryFulfilled.catch((error) => {
          configureScope((scope) => {
            scope.setExtra('error', error?.error || error);
            scope.setExtra('params', params);
            scope.setLevel(Severity.Error);
            scope.setTransactionName(
              `Shopify Integration Error - ${params.shop}`
            );
            scope.setUser({
              username:
                (getState() as RootState).userDetails.loggedInUser ||
                String(params.shop),
            });
          });
          const errorMessage = new Error(
            `${
              (error as any)?.error?.data?.errors ||
              'Failed to perform the task'
            }`
          );
          errorMessage.name = 'Shopify Integration Error';
          captureException(errorMessage);
        });
      },
    }),
  }),
});

export const {
  useGetProductsCohortsQuery,
  useAddShopifyIntegrationMutation,
  useSetStoreSubscriptionChargesMutation,
  useGetUserInfoQuery,
  useUpdateUserNameMutation,
  useUpdateUserEmailMutation,
  useSendAccountVerificationLinkMutation,
  useConfirmEmailChangeMutation,
  useConfirmAccountMutation,
  useGetStoreInfoQuery,
  useAddStoreMemberMutation,
  useRemoveStoreMemberMutation,
  useUpdatePasswordMutation,
  useGetReferralsQuery,
  useSendResetPasswordLinkMutation,
  useSetNewPasswordMutation,
  useConfirmInvitationMutation,
  useGetStoresQuery,
  useGetCustomerCohortsQuery,
  useGetCustomerPurchaseTimingQuery,
  useGetDashboardStoreAggQuery,
  useGetDashboardLifecycleQuery,
  useGetDashboardEmailCampaignQuery,
  useGetEmailTimeSeriesQuery,
  useGetCustomerSegmentsQuery,
  useUpdateSegmentMutation,
  useSyncOnceMutation,
  useSaveSegmentMutation,
  useDeleteSegmentMutation,
  useGetProductJourneyQuery,
  useGetJourneyProductListQuery,
  useGetCustomersListQuery,
  useExportCustomersListMutation,
  useExportMySegmentsListMutation,
  useGetProductJourneySummaryQuery,
  useAddIntegrationMutation,
  useUpdateIntegrationMutation,
  useRemoveIntegrationMutation,
  useAddOAuthIntegrationMutation,
  useAddGoogleOAuthIntegrationMutation,
  useGetKlaviyoListQuery,
  useGetFiltersQuery,
  useGetProductCrossSellQuery,
  useGetProductReplenishmentQuery,
  useGetRelationsProductListQuery,
  useGetRelationsSummaryQuery,
  useGetOnboardingProgressStatusQuery,
  useGetProductImageQuery,
  useGetCustomerStatisticsQuery,
  useGetEmailCampaignRevenueAttributionQuery,
  useUpdateOnboardingSettingsMutation,
  useSelectSubscriptionPlanMutation,
} = segmentsApi;
